import { useState, useEffect, useCallback, useRef } from 'react'
import type { PendingImage, PendingAudio, PendingDocument, ChatAttachment } from '@/components/workflow/WorkflowChat'

const DRAFT_PREFIX = 'chat-draft:'
const MAX_FILE_SIZE_FOR_PERSIST = 10 * 1024 * 1024 // 10MB - don't base64 encode huge files

interface SerializedImage {
  name: string
  type: string
  dataUrl: string // base64 data URL for full restore
}

interface SerializedAudio {
  name: string
  type: string
  size: number
  duration: number
  dataUrl?: string // base64 if small enough
}

interface SerializedDocument {
  name: string
  type: string
  size: number
  dataUrl?: string
}

interface DraftData {
  text: string
  images?: SerializedImage[]
  audio?: SerializedAudio[]
  documents?: SerializedDocument[]
  libraryAttachments?: ChatAttachment[]
  savedAt: number
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

function dataUrlToFile(dataUrl: string, name: string, type: string): File {
  const [header, base64] = dataUrl.split(',')
  const mime = header.match(/:(.*?);/)?.[1] || type
  const bytes = atob(base64)
  const arr = new Uint8Array(bytes.length)
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i)
  return new File([arr], name, { type: mime })
}

function storageKey(id: string) {
  return `${DRAFT_PREFIX}${id}`
}

/** Max age for drafts: 7 days */
const MAX_DRAFT_AGE = 7 * 24 * 60 * 60 * 1000

export function useChatDraft(draftId: string | null) {
  const key = draftId ? storageKey(draftId) : null
  const [loaded, setLoaded] = useState(false)
  const saveTimer = useRef<ReturnType<typeof setTimeout>>()

  // --- State ---
  const [input, setInput] = useState('')
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([])
  const [pendingAudio, setPendingAudio] = useState<PendingAudio[]>([])
  const [pendingDocuments, setPendingDocuments] = useState<PendingDocument[]>([])
  const [pendingLibraryAttachments, setPendingLibraryAttachments] = useState<ChatAttachment[]>([])

  // Load draft from localStorage on mount / key change
  useEffect(() => {
    if (!key) { setLoaded(true); return }
    try {
      const raw = localStorage.getItem(key)
      if (!raw) { setLoaded(true); return }
      const draft: DraftData = JSON.parse(raw)

      // Expire old drafts
      if (Date.now() - draft.savedAt > MAX_DRAFT_AGE) {
        localStorage.removeItem(key)
        setLoaded(true)
        return
      }

      if (draft.text) setInput(draft.text)

      // Restore images from data URLs
      if (draft.images?.length) {
        const restored = draft.images.map(img => {
          const file = dataUrlToFile(img.dataUrl, img.name, img.type)
          return { file, preview: URL.createObjectURL(file) }
        })
        setPendingImages(restored)
      }

      // Restore audio
      if (draft.audio?.length) {
        const restored = draft.audio
          .filter(a => a.dataUrl)
          .map(a => {
            const file = dataUrlToFile(a.dataUrl!, a.name, a.type)
            return { file, duration: a.duration }
          })
        if (restored.length) setPendingAudio(restored)
      }

      // Restore documents
      if (draft.documents?.length) {
        const restored = draft.documents
          .filter(d => d.dataUrl)
          .map(d => {
            const file = dataUrlToFile(d.dataUrl!, d.name, d.type)
            return { file }
          })
        if (restored.length) setPendingDocuments(restored)
      }

      // Restore library attachments (already serializable)
      if (draft.libraryAttachments?.length) {
        setPendingLibraryAttachments(draft.libraryAttachments)
      }
    } catch {
      // Corrupted draft — discard silently
      if (key) localStorage.removeItem(key)
    }
    setLoaded(true)
  }, [key])

  // Save draft to localStorage (debounced)
  const saveDraft = useCallback(async () => {
    if (!key) return

    const hasContent = input.trim() ||
      pendingImages.length > 0 ||
      pendingAudio.length > 0 ||
      pendingDocuments.length > 0 ||
      pendingLibraryAttachments.length > 0

    if (!hasContent) {
      localStorage.removeItem(key)
      return
    }

    try {
      const images: SerializedImage[] = await Promise.all(
        pendingImages.map(async img => ({
          name: img.file.name,
          type: img.file.type,
          dataUrl: await fileToDataUrl(img.file),
        }))
      )

      const audio: SerializedAudio[] = await Promise.all(
        pendingAudio.map(async a => ({
          name: a.file.name,
          type: a.file.type,
          size: a.file.size,
          duration: a.duration,
          dataUrl: a.file.size <= MAX_FILE_SIZE_FOR_PERSIST
            ? await fileToDataUrl(a.file)
            : undefined,
        }))
      )

      const documents: SerializedDocument[] = await Promise.all(
        pendingDocuments.map(async d => ({
          name: d.file.name,
          type: d.file.type,
          size: d.file.size,
          dataUrl: d.file.size <= MAX_FILE_SIZE_FOR_PERSIST
            ? await fileToDataUrl(d.file)
            : undefined,
        }))
      )

      const draft: DraftData = {
        text: input,
        images: images.length ? images : undefined,
        audio: audio.length ? audio : undefined,
        documents: documents.length ? documents : undefined,
        libraryAttachments: pendingLibraryAttachments.length ? pendingLibraryAttachments : undefined,
        savedAt: Date.now(),
      }

      localStorage.setItem(key, JSON.stringify(draft))
    } catch (e) {
      // localStorage full or serialization error — not critical
      console.warn('Failed to save chat draft:', e)
    }
  }, [key, input, pendingImages, pendingAudio, pendingDocuments, pendingLibraryAttachments])

  // Debounced auto-save on state change
  useEffect(() => {
    if (!loaded) return
    clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(saveDraft, 500)
    return () => clearTimeout(saveTimer.current)
  }, [saveDraft, loaded])

  // Synchronous text-only save for beforeunload (async file serialization won't complete in time)
  const saveTextSync = useCallback(() => {
    if (!key) return
    if (!input.trim()) return
    try {
      const existing = localStorage.getItem(key)
      if (existing) {
        const draft: DraftData = JSON.parse(existing)
        draft.text = input
        draft.savedAt = Date.now()
        localStorage.setItem(key, JSON.stringify(draft))
      } else {
        localStorage.setItem(key, JSON.stringify({ text: input, savedAt: Date.now() }))
      }
    } catch { /* not critical */ }
  }, [key, input])

  // Save on unmount / beforeunload
  useEffect(() => {
    if (!loaded) return
    const handleBeforeUnload = () => { saveTextSync() }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      // Component unmount (SPA navigation) — kick off async save
      saveDraft()
    }
  }, [saveDraft, saveTextSync, loaded])

  const clearDraft = useCallback(() => {
    setInput('')
    pendingImages.forEach(img => URL.revokeObjectURL(img.preview))
    setPendingImages([])
    setPendingAudio([])
    setPendingDocuments([])
    setPendingLibraryAttachments([])
    if (key) localStorage.removeItem(key)
  }, [key, pendingImages])

  return {
    input, setInput,
    pendingImages, setPendingImages,
    pendingAudio, setPendingAudio,
    pendingDocuments, setPendingDocuments,
    pendingLibraryAttachments, setPendingLibraryAttachments,
    clearDraft,
    draftLoaded: loaded,
  }
}
